/*
 *  This file candidate-session-sync.service.ts is part of HROne Inbox. The intelectual property owned by Uneecops Workplace Solution PVT. LTD.
 *  CopyrightYear: 2025
 *  (C) 2015-2025. All Right reserved. Uneecops Workplace Solution PVT. LTD.
 */

import { Injectable, inject } from '@angular/core';
import { Observable, Subject, fromEvent, merge } from 'rxjs';
import { filter, map, share } from 'rxjs/operators';
import { SharedService } from './shared.service';

const SESSION_KEYS = ['mob', 'domainCode'];

/**
 * Keeps OTP-protected link pages in sync when the candidate logs in / out from another browser tab.
 * The browser fires `storage` only in *other* tabs, so the tab that did the login/logout is unaffected.
 */
@Injectable({ providedIn: 'root' })
export class CandidateSessionSyncService {
  private readonly _shared = inject(SharedService);
  private loggedOutElsewhere = false;

  // 'storage' never fires in the tab that made the change, so same-tab logouts (avatar menu) are pushed here
  private readonly _localChange = new Subject<void>();

  /** Emits whenever 'mob' / 'domainCode' change in another tab (key null = storage cleared), or on a same-tab logout via notifyLocalChange(). */
  readonly sessionChange$: Observable<void> = merge(
    fromEvent<StorageEvent>(window, 'storage').pipe(
      filter(event => event.key === null || SESSION_KEYS.includes(event.key)),
      map(() => undefined)
    ),
    this._localChange
  ).pipe(share());

  /** Lets open link pages in *this* tab react to a logout done here, the same way other tabs do. */
  notifyLocalChange(): void {
    this._localChange.next();
  }

  isLoggedIn(): boolean {
    return this._shared.isLogin();
  }

  /** Same check the link pages do in ngOnInit: stored mobile + domain code belong to this link's candidate. */
  isSameCandidate(mobileNo: string, domainCode: string): boolean {
    const localMob = this._shared.decodeBase64(localStorage.getItem('mob') ?? '') ?? '';
    const localDC = localStorage.getItem('domainCode') ?? '';
    return !!mobileNo && localMob == mobileNo && localDC == domainCode;
  }

  /** Mobile number from a link's `cid` query param — same parsing the link pages do ("isd-mobile" or last 10 digits). */
  getLinkMobile(cid: string | null | undefined): string {
    if (!cid) return '';
    try {
      const mobile = this._shared.decodeBase64(cid).trim();
      return mobile.includes('-') ? mobile.split('-')[1] : mobile.slice(-10);
    } catch {
      return '';
    }
  }

  /** Called by a workforce page right before it re-shows the OTP step because of a logout in another tab. */
  markLoggedOutElsewhere(): void {
    this.loggedOutElsewhere = true;
  }

  /**
   * True once after markLoggedOutElsewhere() — lets a re-mounted OTP screen skip the automatic
   * OTP send and wait for the user to press RESEND OTP instead.
   */
  consumeLoggedOutElsewhere(): boolean {
    const value = this.loggedOutElsewhere;
    this.loggedOutElsewhere = false;
    return value;
  }
}
